import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Check, X, Filter, Search, Bell, ChevronDown, User, RotateCcw, Loader2, AlertCircle, ChevronLeft, ChevronRight, Layers } from "lucide-react";
import { useNavigate } from "react-router-dom";
import MainLayout from "./MainLayout";
import "../styles/myTeamLeave.css";
import { useAuth } from "../context/AuthContext";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:3030";
const BASE_URL = `${API_BASE}/api/leave-approvals`;
const DROPDOWN_URL = `${API_BASE}/api/dropdowns/values/STATUS`;
const LEAVE_TYPE_URL = `${API_BASE}/api/leave-types`;

function buildHeaders(authToken) {
  const token = authToken || localStorage.getItem("token") || localStorage.getItem("authToken") || localStorage.getItem("accessToken") || localStorage.getItem("jwt") || "";
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function apiFetch(url, options = {}, authToken) {
  const res = await fetch(url, {
    ...options,
    headers: { ...buildHeaders(authToken), ...options.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(err.message || "Request failed");
  }
  return res.json();
}

// function toYMD(d) {
//   if (!d) return "";
//   if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
//   const dt = new Date(d);
//   if (isNaN(dt)) return "";
//   return dt.toISOString().slice(0, 10);
// }
function toYMD(d) {
  if (!d) return "";
  if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  if (typeof d === "string" && d.includes("T")) return d.slice(0, 10);
  const dt = new Date(d);
  if (isNaN(dt)) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fmtDate(d) {
  const ymd = toYMD(d);
  if (!ymd) return "--";
  const date = new Date(ymd + "T00:00:00");
  const local = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  return `${String(local.getDate()).padStart(2, '0')}-${String(local.getMonth() + 1).padStart(2, '0')}-${local.getFullYear()}`;
}
function fmtDateFull(d) {
  return fmtDate(d);
}
function fmtAppliedOn(isoStr) {
  if (!isoStr) return "--";
  const date = new Date(isoStr);
  if (isNaN(date.getTime())) return isoStr;
  const local = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  return `${String(local.getDate()).padStart(2, '0')}-${String(local.getMonth() + 1).padStart(2, '0')}-${local.getFullYear()}`;
}

function mapLeave(row) {
  const name = row.employee_name || "";
  const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const COLORS = ["#6C8EF5", "#F59E0B", "#10B981", "#8B5CF6", "#EC4899", "#0EA5E9", "#F97316"];
  const color = COLORS[(row.employee_id?.charCodeAt(row.employee_id.length - 1) ?? 0) % COLORS.length];
  return {
    id: row.id,
    employeeId: row.employee_id,
    employeeName: name,
    avatar: initials,
    avatarColor: color,
    leaveType: row.leave_type_name || row.leave_type_id,
    startDate: toYMD(row.start_date),
    endDate: toYMD(row.end_date),
    units: Number(row.total_units ?? row.total_days ?? row.no_of_days ?? row.units ?? 0),
    unitType: row.unit_type || "DAY",
    isLop: row.is_lop ?? false,
    status: (row.status || "PENDING").toUpperCase(),
    appliedOn: fmtAppliedOn(row.created_at || row.applied_on || new Date()),
    rawAppliedOn: row.created_at || row.applied_on || row.start_date || new Date().toISOString(),
    reason: row.reason || "",
    payableUnits: Number(row.payable_units ?? row.total_units ?? row.total_days ?? row.units ?? 0),
  };
}

const STATUS_META = {
  PENDING: { label: "Pending", cls: "tr-badge--pending" },
  APPROVED_BY_LEAD: { label: "Approved by Lead", cls: "tr-badge--approved" },
  APPROVED: { label: "Approved", cls: "tr-badge--approved" },
  REJECTED: { label: "Rejected", cls: "tr-badge--rejected" },
  CANCELLED: { label: "Cancelled", cls: "tr-badge--rejected" },
};

function Highlight({ text, query }) {
  if (!query || !text) return <>{text}</>;
  const str = String(text);
  const idx = str.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{str}</>;
  return (
    <>
      {str.slice(0, idx)}
      <mark className="tr-highlight">{str.slice(idx, idx + query.length)}</mark>
      {str.slice(idx + query.length)}
    </>
  );
}

function SummaryCard({ label, count, variant }) {
  const styles = {
    pending: { card: "tr-sum--pending", num: "#D97706" },
    approved: { card: "tr-sum--approved", num: "#059669" },
    rejected: { card: "tr-sum--rejected", num: "#DC2626" },
  };
  return (
    <div className={`tr-summary-card ${styles[variant].card}`}>
      <p className="tr-sum-label">{label}</p>
      <p className="tr-sum-count" style={{ color: styles[variant].num }}>{count}</p>
    </div>
  );
}

function Toast({ toast }) {
  if (!toast) return null;
  const isError = toast.type === "error";
  return (
    <div className={`tr-toast ${isError ? "tr-toast--error" : "tr-toast--success"}`}>
      {isError ? <AlertCircle size={15} /> : <Check size={15} />}
      {toast.msg}
    </div>
  );
}

export default function TeamRequestsPage() {
  const { user, token: authToken } = useAuth();
  const managerId = user?.empId ?? localStorage.getItem("empId") ?? "MGR001";

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [requests, setRequests] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  const [filterStatus, setFilterStatus] = useState("all");
  const [filterLeaveType, setFilterLeaveType] = useState("all");
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const [empSearch, setEmpSearch] = useState("");
  const [showEmpDropdown, setShowEmpDropdown] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const empDropRef = useRef(null);

  const [showFilterSidebar, setShowFilterSidebar] = useState(false);
  const [showGroupByDropdown, setShowGroupByDropdown] = useState(false);
  const [groupBy, setGroupBy] = useState("None"); // Employee, Leave Type, Status, None

  const [selected, setSelected] = useState(null);
  const [modalMode, setModalMode] = useState("review");
  const [rejectReason, setRejectReason] = useState("");
  const [rejectError, setRejectError] = useState(false);
  const [approveNote, setApproveNote] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [fetchingAttachments, setFetchingAttachments] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const toggleSidebar = () => setSidebarOpen(p => !p);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const closeModal = () => {
    setSelected(null);
    setApproveNote("");
    setRejectReason("");
    setRejectError(false);
    setModalMode("review");
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const headers = buildHeaders(authToken);
      const [leavesData, exceptionData, statusData, leaveTypesData] = await Promise.all([
        apiFetch(`${BASE_URL}/pending/${managerId}`, {}, authToken).catch(() => []),
        apiFetch(`${API_BASE}/api/employee-requests/team/${managerId}`, {}, authToken).catch(() => []),
        fetch(DROPDOWN_URL, { headers }).then(r => r.ok ? r.json() : []),
        fetch(LEAVE_TYPE_URL, { headers }).then(r => r.ok ? r.json() : []),
      ]);

      const standardLeaves = (Array.isArray(leavesData) ? leavesData : []).map(mapLeave);
      const exceptionLeaves = (Array.isArray(exceptionData.data) ? exceptionData.data : (Array.isArray(exceptionData) ? exceptionData : [])).map(row => ({
        ...mapLeave(row),
        isException: true,
        leaveType: row.request_type, // Override with correct field
      }));

      setRequests([...standardLeaves, ...exceptionLeaves].sort((a, b) => new Date(b.rawAppliedOn) - new Date(a.rawAppliedOn)));

      const rawStatus = Array.isArray(statusData) ? statusData : (statusData.values || statusData.data || statusData.items || []);
      setStatusOptions(rawStatus.map(opt => {
        if (typeof opt === "string") return { val: opt, label: opt };
        const val = String(opt.dropdown_value_code ?? opt.value ?? opt.id ?? "");
        const label = String(opt.dropdown_value_name ?? opt.label ?? opt.name ?? val);
        return { val, label };
      }));

      const rawTypes = Array.isArray(leaveTypesData) ? leaveTypesData : (leaveTypesData.data || leaveTypesData.items || []);
      setLeaveTypes(rawTypes);
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setLoading(false);
    }
  }, [managerId, authToken]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (selected && selected.id) {
      fetchAttachments(selected.id);
    } else {
      setAttachments([]);
    }
  }, [selected]);

  const fetchAttachments = async (leaveId) => {
    setFetchingAttachments(true);
    try {
      const res = await fetch(`${API_BASE}/api/leave-requests/${leaveId}/attachments`, {
        headers: buildHeaders(authToken)
      });
      if (res.ok) {
        const data = await res.json();
        setAttachments(data || []);
      }
    } catch (err) {
      console.warn("Error fetching attachments:", err);
    } finally {
      setFetchingAttachments(false);
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (empDropRef.current && !empDropRef.current.contains(e.target))
        setShowEmpDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const activeFilterCount = [
    filterStatus !== "all", filterLeaveType !== "all",
    !!filterFromDate, !!filterToDate, !!empSearch.trim(),
  ].filter(Boolean).length;

  const resetFilters = () => {
    setFilterStatus("all"); setFilterLeaveType("all");
    setFilterFromDate(""); setFilterToDate(""); setEmpSearch(""); setPage(1);
  };

  const pendingCount = requests.filter(r => r.status === "PENDING").length;
  const awaitingHeadApproval = requests.filter(r => r.status === "APPROVED_BY_LEAD").length;
  const approvedCount = requests.filter(r => r.status === "APPROVED").length;
  const rejectedCount = requests.filter(r => r.status === "REJECTED").length;

  const leaveTypeOptions = leaveTypes.length > 0
    ? leaveTypes.map(lt => ({ val: lt.name || lt.id, label: lt.name || lt.label }))
    : [...new Set(requests.map(r => r.leaveType))].map(lt => ({ val: lt, label: lt }));

  const nameSuggestions = useMemo(() => {
    if (!empSearch.trim()) return [];
    const q = empSearch.toLowerCase();
    const seen = new Set();
    return requests
      .filter(r => (r.employeeName.toLowerCase().includes(q) || r.employeeId.toLowerCase().includes(q)) && !seen.has(r.employeeName) && seen.add(r.employeeName))
      .map(r => ({ name: r.employeeName, id: r.employeeId }))
      .slice(0, 5);
  }, [empSearch, requests]);

  const filtered = useMemo(() => {
    return requests.filter(r => {
      if (filterStatus !== "all" && r.status !== filterStatus) return false;
      if (filterLeaveType !== "all" && r.leaveType !== filterLeaveType) return false;
      if (filterFromDate && r.startDate && r.startDate < filterFromDate) return false;
      if (filterToDate && r.startDate && r.startDate > filterToDate) return false;
      if (empSearch.trim()) {
        const q = empSearch.toLowerCase();
        if (!r.employeeName.toLowerCase().includes(q) && !r.employeeId.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [requests, filterStatus, filterLeaveType, filterFromDate, filterToDate, empSearch]);

  const sortedAndGrouped = useMemo(() => {
    if (groupBy === "None") return filtered;
    return [...filtered].sort((a, b) => {
      let keyA = "", keyB = "";
      if (groupBy === "Employee") {
        keyA = (a.employeeName || a.employeeId || "").toLowerCase();
        keyB = (b.employeeName || b.employeeId || "").toLowerCase();
      } else if (groupBy === "Leave Type") {
        keyA = (a.leaveType || "").toLowerCase();
        keyB = (b.leaveType || "").toLowerCase();
      } else if (groupBy === "Status") {
        keyA = (a.status || "").toLowerCase();
        keyB = (b.status || "").toLowerCase();
      }
      return keyA.localeCompare(keyB);
    });
  }, [filtered, groupBy]);

  const paginated = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return sortedAndGrouped.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedAndGrouped, page, rowsPerPage]);

  useEffect(() => { setPage(1); }, [filterStatus, filterLeaveType, filterFromDate, filterToDate, empSearch, groupBy]);

  // ── Approve ──
  const handleApprove = async () => {
    setActionLoading(true);
    try {
      if (selected.isException) {
        await apiFetch(`${API_BASE}/api/employee-requests/${selected.id}/approve`, {
          method: "POST",
          body: JSON.stringify({ approver_id: managerId, action: 'APPROVED', approval_note: approveNote }),
        }, authToken);
      } else {
        await apiFetch(`${BASE_URL}/approve/${selected.id}`, {
          method: "PUT",
          body: JSON.stringify({ approval_note: approveNote }),
        }, authToken);
      }
      setRequests(prev => prev.map(r => r.id === selected.id ? { ...r, status: "APPROVED" } : r));
      closeModal();
      showToast("Request approved successfully");
    } catch (err) {
      showToast(err.message || "Failed to approve leave", "error");
    } finally {
      setActionLoading(false);
    }
  };

  // ── Reject ──
  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) { setRejectError(true); return; }
    setActionLoading(true);
    try {
      if (selected.isException) {
        await apiFetch(`${API_BASE}/api/employee-requests/${selected.id}/approve`, {
          method: "POST",
          body: JSON.stringify({ approver_id: managerId, action: 'REJECTED', rejection_reason: rejectReason }),
        }, authToken);
      } else {
        await apiFetch(`${BASE_URL}/reject/${selected.id}`, {
          method: "PUT",
          body: JSON.stringify({ rejection_reason: rejectReason }),
        }, authToken);
      }
      setRequests(prev => prev.map(r => r.id === selected.id ? { ...r, status: "REJECTED" } : r));
      closeModal();
      showToast("Request rejected");
    } catch (err) {
      showToast(err.message || "Failed to reject leave", "error");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <MainLayout sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} title="My Team Leave">
      <div className="hamburger" onClick={toggleSidebar}>☰</div>
      <div className="tr-wrap" style={{ padding: 0 }}>

        <div className="tr-summary-grid">
          <SummaryCard label="PENDING" count={pendingCount} variant="pending" />
          <SummaryCard label="AWAITING HEAD APPROVAL" count={awaitingHeadApproval} variant="pending" />
          <SummaryCard label="APPROVED" count={approvedCount} variant="approved" />
          <SummaryCard label="REJECTED" count={rejectedCount} variant="rejected" />
        </div>

        {/* Action Bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 14px', flex: '1', minWidth: '200px', maxWidth: '420px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <Search size={16} color="#94a3b8" style={{ flexShrink: 0 }} />
            <input
              style={{ border: 'none', outline: 'none', padding: '4px 10px', width: '100%', fontSize: '14px', color: '#1e293b', background: 'transparent' }}
              placeholder="Search by name or ID..."
              value={empSearch}
              onChange={e => { setEmpSearch(e.target.value); setShowEmpDropdown(true); }}
              onFocus={() => setShowEmpDropdown(true)}
              autoComplete="off"
            />
            {empSearch && (
              <button onClick={() => { setEmpSearch(''); setShowEmpDropdown(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, display: 'flex' }}>
                <X size={14} />
              </button>
            )}
            {showEmpDropdown && nameSuggestions.length > 0 && (
              <div className="tr-autocomplete">
                {nameSuggestions.map(s => (
                  <button key={s.id + s.name} className="tr-autocomplete-item"
                    onClick={() => { setEmpSearch(s.name); setShowEmpDropdown(false); }}>
                    <span className="tr-ac-avatar">{s.name.split(" ").map(w => w[0]).join("").slice(0, 2)}</span>
                    <div>
                      <span className="tr-ac-name"><Highlight text={s.name} query={empSearch} /></span>
                      <span className="tr-ac-id"><Highlight text={s.id} query={empSearch} /></span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            {/* Filter Button */}
            <div style={{ position: 'relative' }}>
              <button onClick={() => setShowFilterSidebar(true)} style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 14px', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#334155' }}>
                <Filter size={14} color="#64748b" /> Filter {activeFilterCount > 0 && <span style={{ background: '#3b82f6', color: '#fff', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>{activeFilterCount}</span>}
              </button>
            </div>

            {/* Group By Button */}
            <div style={{ position: 'relative' }}>
              <button onClick={() => setShowGroupByDropdown(!showGroupByDropdown)} style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 14px', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#334155' }}>
                <Layers size={14} color="#64748b" /> Group by
              </button>
              {showGroupByDropdown && (
                <div style={{ position: 'absolute', top: '100%', right: '0', marginTop: '8px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 0', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, minWidth: '160px' }}>
                  {["Employee", "Leave Type", "Status", "None"].map(opt => (
                    <div
                      key={opt}
                      onClick={() => { setGroupBy(opt); setShowGroupByDropdown(false); }}
                      style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: groupBy === opt ? '#f1f5f9' : 'transparent', color: groupBy === opt ? '#0f172a' : '#475569' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = groupBy === opt ? '#f1f5f9' : 'transparent'}
                    >
                      {opt}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filter Sidebar */}
        {showFilterSidebar && (
          <>
            <div className="tr-modal-backdrop" onClick={() => setShowFilterSidebar(false)} style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.4)', zIndex: 999 }} />
            <div style={{ position: 'fixed', top: 0, right: 0, width: '340px', height: '100vh', background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.1)', zIndex: 1000, display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.3s ease' }}>
              <style>{`
                @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
              `}</style>
              <div style={{ padding: '24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#0f172a' }}>Filters</h3>
                <button onClick={() => setShowFilterSidebar(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={20} /></button>
              </div>

              <div style={{ padding: '24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Status */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Status</label>
                  <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', backgroundColor: '#f8fafc' }}>
                    <option value="all">All Status</option>
                    {statusOptions.map((opt, i) => (
                      <option key={opt.val + i} value={opt.val}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Leave Type */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Leave Type</label>
                  <select value={filterLeaveType} onChange={e => setFilterLeaveType(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', backgroundColor: '#f8fafc' }}>
                    <option value="all">All Types</option>
                    {leaveTypeOptions.map((lt, i) => (
                      <option key={lt.val + i} value={lt.val}>{lt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Date Range */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Date Range</label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>From</label>
                      <input type="date" value={filterFromDate} max={filterToDate || undefined} onChange={e => setFilterFromDate(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px', backgroundColor: '#f8fafc' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>To</label>
                      <input type="date" value={filterToDate} min={filterFromDate || undefined} onChange={e => setFilterToDate(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px', backgroundColor: '#f8fafc' }} />
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ padding: '20px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: '12px' }}>
                <button onClick={resetFilters} style={{ flex: 1, padding: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#fff', border: '1px solid #fee2e2', borderRadius: '8px', gap: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#dc2626' }}>
                  <RotateCcw size={14} /> Reset
                </button>
                <button onClick={() => setShowFilterSidebar(false)} style={{ flex: 1, padding: '10px', background: '#3b82f6', border: 'none', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                  Apply
                </button>
              </div>
            </div>
          </>
        )}

        <div className="tr-table-card">
          <div className="tr-table-scroll">
            {loading && (
              <div className="tr-empty">
                <Loader2 size={28} className="tr-spinner" />
                <p>Loading leave requests…</p>
              </div>
            )}
            {!loading && fetchError && (
              <div className="tr-empty tr-empty--error">
                <AlertCircle size={28} />
                <p>{fetchError}</p>
                <button className="tr-review-btn" onClick={fetchAll}>Retry</button>
              </div>
            )}
            {!loading && !fetchError && filtered.length === 0 && (
              <div className="tr-empty"><p>No matching requests</p></div>
            )}
            {!loading && !fetchError && filtered.length > 0 && (
              <>
                <table className="tr-table">
                  <thead>
                    <tr>
                      {["EMPLOYEE", "EMP ID", "LEAVE TYPE", "DATE RANGE", "UNITS", "LOP", "STATUS", "ACTIONS"].map(h => (
                        <th key={h} className="tr-th">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((req, i) => {
                      let currentGroup = null;
                      if (groupBy !== "None") {
                        if (groupBy === "Employee") {
                          currentGroup = req.employeeName || req.employeeId || "--";
                        } else if (groupBy === "Leave Type") {
                          currentGroup = req.leaveType || "--";
                        } else if (groupBy === "Status") {
                          currentGroup = (req.status || "").toUpperCase();
                        }
                      }

                      let prevGroup = null;
                      if (i > 0 && groupBy !== "None") {
                        const prevReq = paginated[i - 1];
                        if (groupBy === "Employee") {
                          prevGroup = prevReq.employeeName || prevReq.employeeId || "--";
                        } else if (groupBy === "Leave Type") {
                          prevGroup = prevReq.leaveType || "--";
                        } else if (groupBy === "Status") {
                          prevGroup = (prevReq.status || "").toUpperCase();
                        }
                      }

                      const showGroupHeader = groupBy !== "None" && currentGroup !== prevGroup;

                      return (
                        <React.Fragment key={req.id}>
                          {showGroupHeader && (
                            <tr>
                              <td colSpan="8" style={{ background: '#f8fafc', fontWeight: 'bold', borderBottom: '1px solid #e2e8f0', padding: '12px 16px', color: '#334155' }}>
                                {currentGroup}
                              </td>
                            </tr>
                          )}
                          <tr className="tr-row" style={{ animationDelay: `${i * 40}ms` }}>
                            <td className="tr-td">
                              <div className="tr-emp">
                                <span className="tr-avatar" style={{ background: req.avatarColor }}>{req.avatar}</span>
                                <div className="tr-emp-name-wrapper" style={{ display: 'flex', flexDirection: 'column' }}>
                                  <span className="tr-emp-name"><Highlight text={req.employeeName} query={empSearch} /></span>
                                  {req.isException && (
                                    <span style={{ fontSize: '10px', background: '#F97316', color: '#FFF', padding: '2px 6px', borderRadius: '4px', marginTop: '2px', width: 'fit-content', fontWeight: 'bold' }}>
                                      EXCEPTION
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="tr-td">
                              <span className="tr-empid-badge"><Highlight text={req.employeeId} query={empSearch} /></span>
                            </td>
                            <td className="tr-td tr-td--muted">{req.leaveType}</td>
                            <td className="tr-td tr-td--muted">{fmtDate(req.startDate)} – {fmtDate(req.endDate)}</td>
                            <td className="tr-td tr-td--muted">
                              {req.units} {req.unitType === "HOUR" ? "hrs" : req.units === 1 ? "day" : "days"}
                            </td>
                            <td className="tr-td">
                              {req.isLop ? <span className="tr-lop-badge">LOP</span> : <span className="tr-td--muted">--</span>}
                            </td>
                            <td className="tr-td">
                              <span className={`tr-status-badge ${STATUS_META[req.status]?.cls ?? ""}`}>
                                {STATUS_META[req.status]?.label ?? req.status}
                              </span>
                            </td>
                            <td className="tr-td">
                              {(req.status === "PENDING" || req.status === "APPROVED_BY_LEAD")
                                ? <button className="tr-review-btn" onClick={() => { setSelected(req); setModalMode("review"); setRejectReason(""); setRejectError(false); setApproveNote(""); }}>
                                  <Check size={13} /> Review
                                </button>
                                : <span className="tr-td--muted">--</span>
                              }
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>

        {/* Pagination */}
        {!loading && !fetchError && filtered.length > 0 && (
          <div className="em-pagination">
            <div className="em-pagination__left">
              <span className="em-pagination__info">
                Showing {Math.min((page - 1) * rowsPerPage + 1, filtered.length)}–{Math.min(page * rowsPerPage, filtered.length)} of {filtered.length}
              </span>
              <div className="em-rows-selector">
                <span className="em-rows-label">Rows:</span>
                <select
                  className="em-rows-select"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={30}>30</option>
                  <option value={40}>40</option>
                </select>
              </div>
            </div>
            <div className="em-pagination__btns">
              <button className="em-page-btn" disabled={page === 1} onClick={() => setPage(page - 1)}><ChevronLeft size={15} /></button>
              {Array.from({ length: Math.ceil(filtered.length / rowsPerPage) }, (_, i) => (
                <button key={i + 1} className={`em-page-btn${page === i + 1 ? " active" : ""}`} onClick={() => setPage(i + 1)}>{i + 1}</button>
              ))}
              <button className="em-page-btn" disabled={page === Math.ceil(filtered.length / rowsPerPage)} onClick={() => setPage(page + 1)}><ChevronRight size={15} /></button>
            </div>
          </div>
        )}
      </div>
      {/* ── Modal ── */}
      {selected && (
        <div className="tr-modal-backdrop" onClick={() => !actionLoading && closeModal()}>
          <div className="tr-modal" onClick={e => e.stopPropagation()}>
            <div className="tr-modal-header">
              <div>
                <h2 className="tr-modal-title">
                  {modalMode === "reject" ? "Reject Request" : "Review Leave Request"}
                </h2>
                <p className="tr-modal-sub">
                  {modalMode === "reject"
                    ? "Please provide a reason for rejection."
                    : "Review the leave request details and approve or reject."}
                </p>
              </div>
              <button className="tr-modal-close" onClick={() => !actionLoading && closeModal()}>
                <X size={18} />
              </button>
            </div>

            {modalMode === "review" && (
              <div className="tr-modal-body">
                <div className="tr-modal-emp">
                  <span className="tr-avatar tr-avatar--lg" style={{ background: selected.avatarColor }}>{selected.avatar}</span>
                  <div>
                    <p className="tr-modal-emp-name">{selected.employeeName}</p>
                    <p className="tr-modal-emp-sub">
                      <span className="tr-empid-badge">{selected.employeeId}</span>
                      &nbsp;· Applied {selected.appliedOn}
                    </p>
                  </div>
                </div>
                <div className="tr-modal-details">
                  {[
                    { label: "Leave Type", value: selected.leaveType },
                    { label: "Date Range", value: `${fmtDateFull(selected.startDate)} - ${fmtDateFull(selected.endDate)}` },
                    { label: "Total Units", value: `${selected.units} ${selected.unitType === "HOUR" ? "hrs" : "days"}` },
                    { label: "Payable Units", value: `${selected.payableUnits} ${selected.unitType === "HOUR" ? "hrs" : "days"}` },
                    { label: "Reason", value: selected.reason },
                  ].map(({ label, value }) => (
                    <div key={label} className="tr-detail-row">
                      <span className="tr-detail-label">{label}</span>
                      <span className="tr-detail-value">{value}</span>
                    </div>
                  ))}
                </div>

                {/* ── Optional Approval Note ── */}
                <div className="tr-reject-wrap" style={{ padding: "12px 24px" }}>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#6b7280", display: "block", marginBottom: "6px" }}>
                    Approval Note <span style={{ fontWeight: 400, color: "#9ca3af" }}>(Optional)</span>
                  </label>
                  <textarea
                    className="tr-reject-input"
                    placeholder="Add a note for the employee (optional)..."
                    value={approveNote}
                    rows={3}
                    onChange={e => setApproveNote(e.target.value)}
                  />
                </div>

                {/* ── Attachments ── */}
                <div className="tr-modal-attachments" style={{ marginTop: "8px", padding: "0 24px 16px" }}>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#6b7280", display: "block", marginBottom: "8px" }}>
                    Attachments
                  </label>
                  {fetchingAttachments ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#6b7280' }}>
                      <Loader2 size={14} className="tr-spinner" /> Loading attachments...
                    </div>
                  ) : attachments.length > 0 ? (
                    <div className="attach-grid">
                      {attachments.map((att) => (
                        <div key={att.id} className="attach-card" onClick={() => att.mime_type.startsWith('image/') && setPreviewImage(att.signed_url)}>
                          <div className="attach-preview">
                            {att.mime_type === 'application/pdf' ? (
                              <a href={att.signed_url} target="_blank" rel="noopener noreferrer" className="pdf-link">📄 PDF</a>
                            ) : (
                              <img src={att.signed_url} alt={att.file_name} />
                            )}
                          </div>
                          <div className="attach-info">
                            <span className="attach-name" title={att.file_name}>{att.file_name}</span>
                            <span className="attach-size">({att.file_size})</span>
                          </div>
                          {att.mime_type === 'application/pdf' && (
                            <a href={att.signed_url} target="_blank" rel="noopener noreferrer" className="attach-open-link" onClick={e => e.stopPropagation()}>
                              ↗
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: "13px", color: "#9ca3af", margin: 0 }}>No attachments provided</p>
                  )}
                </div>
              </div>
            )}

            {modalMode === "reject" && (
              <div className="tr-reject-wrap">
                <textarea
                  className={`tr-reject-input ${rejectError ? "tr-reject-input--error" : ""}`}
                  placeholder="Enter rejection reason..."
                  value={rejectReason}
                  rows={4}
                  onChange={e => { setRejectReason(e.target.value); setRejectError(false); }}
                />
                {rejectError && <p className="tr-reject-error">Please enter a reason before confirming.</p>}
              </div>
            )}

            <div className="tr-modal-footer">
              {modalMode === "review" ? (
                <>
                  <button className="tr-btn tr-btn--outline" onClick={() => setModalMode("reject")} disabled={actionLoading}>
                    <X size={14} /> Reject
                  </button>
                  <button className="tr-btn tr-btn--approve" onClick={handleApprove} disabled={actionLoading}>
                    {actionLoading ? <Loader2 size={14} className="tr-spinner" /> : <Check size={14} />}
                    {actionLoading ? "Approving…" : "Approve"}
                  </button>
                </>
              ) : (
                <>
                  <button className="tr-btn tr-btn--outline" onClick={() => setModalMode("review")} disabled={actionLoading}>
                    Back
                  </button>
                  <button className="tr-btn tr-btn--danger" onClick={handleRejectConfirm} disabled={actionLoading}>
                    {actionLoading ? <Loader2 size={14} className="tr-spinner" /> : null}
                    {actionLoading ? "Rejecting…" : "Confirm Rejection"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />

      {/* Preview Modal */}
      {previewImage && (
        <div className="image-preview-modal" onClick={() => setPreviewImage(null)}>
          <div className="preview-modal-content" onClick={e => e.stopPropagation()}>
            <button className="preview-close" onClick={() => setPreviewImage(null)}>✕</button>
            <img src={previewImage} alt="Full preview" />
          </div>
        </div>
      )}
    </MainLayout>
  );
}
